import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-dropdown-select";
import PageTitle from "../../Components/PageTitle";
import { getTypeOfClaimList } from "../../redux/actions/TypeOfClaimActions";
import { getChecklistList } from "../../redux/actions/ChecklistActions";
import { 
  addUserFeeStructure, 
  deleteUserFeeStructure, 
  updateUserFeeStructure, 
  getUserFeeStructureById 
} from "../../redux/actions/UserActions";
import swal from 'sweetalert2';

const FeeStructure = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth },
    typeOfClaimReducer: { typeOfClaimLists, loadingTypeOfClaim },
    checklistReducer: { checklistLists, loadingChecklist },
    userReducer: { userFeeStructureSingleList, loadingUser, changedUserFeeStructure },
  } = useSelector((state) => state);

  const [formData, setFormData] = useState({
    typeOfClaimId: null,
    checklistId: null,
    amount: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [validated, setValidated] = useState(false);

  // Fetch Type of Claim and Checklist lists on component mount
  useEffect(() => {
    if (auth?.token && id) {
      dispatch(getTypeOfClaimList(auth.token, 1000, 1, "", "asc"));
      dispatch(getChecklistList(auth.token, 1000, 1, "", "asc"));
    }
  }, [auth?.token, dispatch, id]);

  // Fetch User Fee Structure by user ID
  useEffect(() => {
    if (auth?.token && id) {
      dispatch(getUserFeeStructureById(auth.token, id));
    }
  }, [auth?.token, dispatch, id]);

  // Handle success messages for Add and Update operations
  useEffect(() => {
    if (changedUserFeeStructure?.success && auth?.token && id) {
      const message = changedUserFeeStructure?.message || "Operation completed successfully";
      swal.fire({
        show: true,
        title: "Well done!",
        text: message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      
      // Reset form and refresh list
      resetForm();
      dispatch(getUserFeeStructureById(auth.token, id));
      dispatch({ type: 'RESET_DELETE_USER_FEE_STRUCTURE_STATUS' });
    }
  }, [changedUserFeeStructure, auth?.token, id, dispatch]);

  // Handle success message for Delete operation
  useEffect(() => {
    if (changedUserFeeStructure?.status && changedUserFeeStructure?.message && auth?.token && id) {
      swal.fire({
        show: true,
        title: "Deleted!",
        text: changedUserFeeStructure?.message,
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });
      
      // Refresh list
      dispatch(getUserFeeStructureById(auth.token, id));
      dispatch({ type: 'RESET_DELETE_USER_FEE_STRUCTURE_STATUS' });
    }
  }, [changedUserFeeStructure?.status, changedUserFeeStructure?.message, auth?.token, id, dispatch]);

  // Memoized options for dropdowns
  const claimOptions = useMemo(
    () => (typeOfClaimLists || []).map((c) => ({ label: c?.name, value: c?.id })),
    [typeOfClaimLists]
  );

  const checklistOptions = useMemo(
    () => (checklistLists || []).map((c) => ({ 
      label: c?.name, 
      value: c?.id, 
      fee_amount: c?.fee_amount 
    })),
    [checklistLists]
  );

  // Convert single object to array if needed (handle both array and single object responses)
  const feeStructureList = useMemo(() => {
    if (!userFeeStructureSingleList) return [];
    // If it's already an array, return it
    if (Array.isArray(userFeeStructureSingleList)) {
      return userFeeStructureSingleList;
    }
    // If it's a single object, convert to array
    return [userFeeStructureSingleList];
  }, [userFeeStructureSingleList]);

  // Reset form function
  const resetForm = () => {
    setFormData({ typeOfClaimId: null, checklistId: null, amount: "" });
    setEditingId(null);
    setValidated(false);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle Type of Claim selection
  const handleClaimChange = (vals) => {
    setFormData((prev) => ({ 
      ...prev, 
      typeOfClaimId: vals?.[0]?.value || null 
    }));
  };

  // Handle Checklist selection - Auto-fill amount only when adding (not editing)
  const handleChecklistChange = (vals) => {
    const selectedChecklist = vals?.[0];
    const newFormData = {
      ...formData,
      checklistId: selectedChecklist?.value || null,
    };
    
    // Auto-fill amount only when NOT editing (i.e., when adding new entry)
    if (!editingId && selectedChecklist?.fee_amount) {
      newFormData.amount = selectedChecklist.fee_amount;
    }
    
    setFormData(newFormData);
  };

  // Handle Add operation
  const handleAdd = (e) => {
    e.preventDefault();
    setValidated(true);
    
    if (!formData.typeOfClaimId || !formData.checklistId || !formData.amount) {
      return;
    }

    const entry = {
      user_id: id,
      type_of_claim_id: formData.typeOfClaimId,
      checklist_id: formData.checklistId,
      fee_amount: Number(formData.amount),
    };
    
    dispatch(addUserFeeStructure(auth.token, entry));
  };

  // Handle Update operation
  const handleUpdate = (e) => {
    e.preventDefault();
    setValidated(true);
    
    if (!formData.typeOfClaimId || !formData.checklistId || !formData.amount || !editingId) {
      return;
    }

    const updateData = {
      user_id: id,
      type_of_claim_id: formData.typeOfClaimId,
      checklist_id: formData.checklistId,
      fee_amount: Number(formData.amount),
    };
    
    dispatch(updateUserFeeStructure(auth.token, updateData, editingId));
  };

  // Handle Edit - Populate form with row data
  const handleEditFeeStructure = (item) => {
    setEditingId(item.id);
    setFormData({
      typeOfClaimId: item.type_of_claim_id || item.typeOfClaimId,
      checklistId: item.checklist_id || item.checklistId,
      amount: item.fee_amount || item.amount || "",
    });
    setValidated(false);
  };

  // Handle Delete with confirmation
  const handleDeleteFeeStructure = (itemId) => {
    swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteUserFeeStructure(auth.token, itemId));
      }
    });
  };

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "Users", path: "/Active-Users" },
          { label: "Fee Structure", path: `/Fee-Structure/${id}`, active: true },
        ]}
        title={`Fee Structure`}
      />

      <Row>
        <Col lg={12}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="header-title mb-0">User Fee Structure</h4>
              </div>

              <Form onSubmit={editingId ? handleUpdate : handleAdd}>
                <Row>
                  <Col lg={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Type of Claim</Form.Label>
                      <Select
                        options={claimOptions}
                        values={claimOptions.filter((o) => o.value === formData.typeOfClaimId)}
                        onChange={handleClaimChange}
                        loading={loadingTypeOfClaim}
                        placeholder="Select type of claim"
                      />
                      {validated && !formData.typeOfClaimId && (
                        <div className="text-danger small mt-1">Please select a type of claim</div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Checklist</Form.Label>
                      <Select
                        options={checklistOptions}
                        values={checklistOptions.filter((o) => o.value === formData.checklistId)}
                        onChange={handleChecklistChange}
                        loading={loadingChecklist}
                        placeholder="Select checklist"
                      />
                      {validated && !formData.checklistId && (
                        <div className="text-danger small mt-1">Please select a checklist</div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Fee Amount (INR)</Form.Label>
                      <Form.Control
                        required
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Enter amount"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                      />
                      {validated && !formData.amount && (
                        <Form.Control.Feedback type="invalid">
                          Please provide a valid amount.
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex gap-2">
                  <Button type="submit" variant={editingId ? "warning" : "primary"}>
                    <i className={`mdi ${editingId ? 'mdi-content-save' : 'mdi-clipboard-plus-outline'} me-1`}></i>
                    {editingId ? 'Update' : 'Add'}
                  </Button>
                  {editingId && (
                    <Button 
                      type="button" 
                      variant="secondary" 
                      onClick={resetForm}
                    >
                      Cancel
                    </Button>
                  )}
                  <Link to="/Active-Users" className="btn btn-outline-secondary">
                    Back to Users
                  </Link>
                </div>
              </Form>

              {feeStructureList && feeStructureList.length > 0 ? (
                <div className="mt-4" style={{ overflowY: 'auto', maxHeight: '400px' }}>
                  <Table className="mb-0 table-bordered" size="sm" striped responsive>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Type of Claim</th>
                        <th>Checklist</th>
                        <th>Fee Amount (INR)</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeStructureList.map((row, idx) => {
                        const claimId = row.type_of_claim_id || row.typeOfClaimId;
                        const checklistId = row.checklist_id || row.checklistId;
                        // Handle both nested object and direct properties
                        const claimLabel = claimOptions.find((o) => o.value === claimId)?.label 
                          || row.claimType?.name 
                          || row.type_of_claim?.name 
                          || "-";
                        const checklistLabel = checklistOptions.find((o) => o.value === checklistId)?.label 
                          || row.checklist?.name 
                          || "-";
                        const feeAmount = row.fee_amount || row.amount || 0;
                        
                        return (
                          <tr key={row.id || `${claimId}-${checklistId}-${idx}`}>
                            <td>{idx + 1}</td>
                            <td>{claimLabel}</td>
                            <td>{checklistLabel}</td>
                            <td>{feeAmount}</td>
                            <td>
                              <Link 
                                to="#" 
                                className="action-icon text-info me-2" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditFeeStructure(row);
                                }}
                              >
                                <i className="mdi mdi-pencil"></i>
                              </Link>
                              <Link 
                                to="#" 
                                className="action-icon text-danger" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDeleteFeeStructure(row.id);
                                }}
                              >
                                <i className="mdi mdi-delete"></i>
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                !loadingUser && (
                  <div className="mt-4 text-center text-muted">
                    <p>No fee structure data found</p>
                  </div>
                )
              )}

              {loadingUser && (
                <div className="mt-4 text-center">
                  <p>Loading...</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default FeeStructure;
